/// <amd-module name="Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettings" />
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { BoardCardSettings } from "Wit/Clients/Work/Contracts/Work";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import {
    DefaultBoardsCoreFieldSettings,
    FieldSettingKeys
} from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettingsConstants";
import { validateAdditionalFields } from "Wit/Common/WitSettingsViews/Components/FieldSettingsView";
import { IFieldReferenceSetting, IFieldSettings } from "Wit/Common/WitSettingsViewsTypes/Components/FieldSettingsView.props";

export interface IBoardFieldSettings {
    isDirty: IObservableValue<boolean>;
    isValid: IObservableValue<boolean>;
    workItemTypes: string[];

    addCoreFieldForType(workItemType: string, effortFieldName: string): void;
    getErrorMessage(workItemType: string): IObservableValue<string | undefined> | undefined;
    getFieldSettings(workItemType: string): IFieldSettings | undefined;
    getIsValid(workItemType: string): IObservableValue<boolean> | undefined;
    getServerFieldSettings(): BoardCardSettings;
    setErrorMessage(workItemType: string, errorMessage: string): void;
    updateFieldSettings(workItemType: string, fieldSettings: IFieldSettings): void;
}

export type CardSettingsDict = {
    [key: string]: { [key: string]: string }[];
};

/**
 * Board field settings class that manages state, validity, and dirtiness
 */
export class BoardFieldSettings implements IBoardFieldSettings {
    private cardSettings: CardSettingsDict;
    private childTypes: Set<string> | undefined;
    private coreFields = new Set([...DefaultBoardsCoreFieldSettings]);
    private fieldSettingsByType = new Map<string, IFieldSettings>();
    private fieldSettingsErrorMessageByType = new Map<string, IObservableValue<string | undefined>>();
    private fieldSettingsValidityByType = new Map<string, IObservableValue<boolean>>();
    private originalFieldSettings = new Map<string, IFieldSettings>();

    public isDirty = new ObservableValue(false);
    public isValid = new ObservableValue(true);

    constructor(cardSettings: CardSettingsDict, childTypes?: Set<string>) {
        this.cardSettings = cardSettings;
        this.childTypes = childTypes;

        this.coreFields.add(FieldSettingKeys.ShowEmptyFields);
        for (const type of Object.keys(this.cardSettings)) {
            const settings = this.cardSettings[type];
            if (!settings) {
                continue;
            }

            // maintain a copy of the settings in their current state and in their original state to calculate dirtiness
            this.fieldSettingsByType.set(type, initializeFieldSettingsForType(settings, [...this.coreFields]));
            this.originalFieldSettings.set(type, initializeFieldSettingsForType(settings, [...this.coreFields]));
            this.fieldSettingsValidityByType.set(type, new ObservableValue(true));
            this.fieldSettingsErrorMessageByType.set(type, new ObservableValue(undefined));
        }
    }

    public get workItemTypes(): string[] {
        return [...this.fieldSettingsByType.keys()];
    }

    public addCoreFieldForType(workItemType: string, referenceName: string) {
        // taskboard only shows additional core fields for child types
        if (this.childTypes && !this.childTypes.has(workItemType)) {
            return;
        }

        const settings = this.cardSettings[workItemType];
        const fieldSettings = this.fieldSettingsByType.get(workItemType);
        if (!settings || !fieldSettings || !!fieldSettings.coreFields[referenceName]) {
            return;
        }

        const isEnabled = settings.some(settings => {
            const settingsDict = settings as unknown as { [key: string]: string };
            return settingsDict[FieldSettingKeys.FieldIdentifier] === referenceName;
        });

        this.coreFields.add(referenceName);
        fieldSettings.coreFields[referenceName] = new ObservableValue(isEnabled);
        fieldSettings.additionalFields.removeAll(item => item.referenceName === referenceName);
    }

    public getErrorMessage(workItemType: string): IObservableValue<string | undefined> | undefined {
        return this.fieldSettingsErrorMessageByType.get(workItemType);
    }

    public getFieldSettings(workItemType: string): IFieldSettings | undefined {
        return this.fieldSettingsByType.get(workItemType);
    }

    public getIsValid(workItemType: string): IObservableValue<boolean> | undefined {
        return this.fieldSettingsValidityByType.get(workItemType);
    }

    public getServerFieldSettings(): BoardCardSettings {
        const settings: { [type: string]: any } = {};
        for (const [type, fieldSetting] of this.fieldSettingsByType) {
            settings[type] = getServerFieldSettingsForType(fieldSetting);
        }

        return { cards: settings };
    }

    public setErrorMessage(workItemType: string, errorMessage: string): void {
        const errorMessageForType = this.getErrorMessage(workItemType);
        if (errorMessageForType) {
            errorMessageForType.value = errorMessage;
        }
    }

    public updateFieldSettings(workItemType: string, fieldSettings: IFieldSettings): void {
        this.fieldSettingsByType.set(workItemType, fieldSettings);
        const updatedErrorMessage = validateAdditionalFields(fieldSettings.additionalFields.value);
        const errorMessage = this.fieldSettingsErrorMessageByType.get(workItemType);
        if (errorMessage && errorMessage.value !== updatedErrorMessage) {
            errorMessage.value = updatedErrorMessage;
        }

        const updatedIsValid = updatedErrorMessage === undefined;
        this.updateIsValidForType(workItemType, updatedIsValid);
        this.updateIsDirty();
    }

    private updateIsDirty(): void {
        const isDirty = !!this.workItemTypes.find(
            type => !areFieldSettingsEqual(this.fieldSettingsByType.get(type), this.originalFieldSettings.get(type))
        );
        if (this.isDirty.value !== isDirty) {
            this.isDirty.value = isDirty;
        }
    }

    private updateIsValidForType(type: string, isValid: boolean): void {
        const isValidObservable = this.fieldSettingsValidityByType.get(type);
        if (isValidObservable && isValidObservable.value !== isValid) {
            isValidObservable.value = isValid;
            this.updateIsValid();
        }
    }

    private updateIsValid(): void {
        const isValid = ![...this.fieldSettingsValidityByType.values()].find(observable => !observable.value);
        if (isValid !== this.isValid.value) {
            this.isValid.value = isValid;
        }
    }
}

function initializeFieldSettingsForType(initialSettings: { [key: string]: string }[], coreFields: string[]): IFieldSettings {
    const additionalFields: IFieldReferenceSetting[] = [];
    const coreFieldSettings: { [fieldKey: string]: IObservableValue<boolean> } = {};

    for (const coreField of coreFields) {
        coreFieldSettings[coreField] = new ObservableValue(false);
    }

    for (const setting of initialSettings) {
        // FieldSetting is actually a dictionary of KVP :-)
        const settingAsDict = setting as unknown as { [key: string]: string };
        const referenceName = settingAsDict[FieldSettingKeys.FieldIdentifier];
        if (referenceName && !settingAsDict[FieldSettingKeys.DisplayType] && !coreFieldSettings[referenceName]) {
            // additional field
            if (referenceName === CoreFieldRefNames.Title) {
                // title is always shown and isn't configurable
                continue;
            }

            additionalFields.push({ id: newGuid(), referenceName });
        } else {
            // core field
            if (referenceName) {
                const coreFieldObservable = coreFieldSettings[referenceName];
                if (coreFieldObservable) {
                    coreFieldObservable.value = true;
                }
            } else {
                const showEmptyFieldsObservable = coreFieldSettings[FieldSettingKeys.ShowEmptyFields];
                const showEmptyFieldsValue = settingAsDict[FieldSettingKeys.ShowEmptyFields];
                if (showEmptyFieldsObservable && showEmptyFieldsValue) {
                    showEmptyFieldsObservable.value = showEmptyFieldsValue === "true";
                }
            }
        }
    }

    return {
        additionalFields: new ObservableArray(additionalFields),
        coreFields: coreFieldSettings
    };
}

function areFieldSettingsEqual(settingsA?: IFieldSettings, settingsB?: IFieldSettings): boolean {
    if (!settingsA || !settingsB) {
        return false;
    }

    const additionalFieldsA = settingsA.additionalFields.value.map(f => f.referenceName);
    const additionalFieldsB = settingsB.additionalFields.value.map(f => f.referenceName);
    if (areAdditionalFieldsDirty(additionalFieldsA, additionalFieldsB)) {
        return false;
    }

    for (const fieldKey of Object.keys(settingsA.coreFields)) {
        const fieldValueA = settingsA.coreFields[fieldKey];
        const fieldValueB = settingsB.coreFields[fieldKey];
        if (fieldValueA?.value !== fieldValueB?.value) {
            return false;
        }
    }

    return true;
}

function areAdditionalFieldsDirty(currentFields: string[], originalFields: string[]) {
    if (currentFields.length !== originalFields.length) {
        return true;
    }

    return currentFields.findIndex((fieldRefName, index) => fieldRefName !== originalFields[index]) >= 0;
}

function getServerFieldSettingsForType(fieldSettings: IFieldSettings): { [key: string]: string }[] {
    let serverFieldSettings = [];
    let addedTitleField = false;
    for (const [fieldName, value] of Object.entries(fieldSettings.coreFields)) {
        if (fieldName === FieldSettingKeys.ShowEmptyFields) {
            serverFieldSettings.push({ [FieldSettingKeys.ShowEmptyFields]: value.value.toString() });
        } else if (value.value) {
            serverFieldSettings.push({ [FieldSettingKeys.FieldIdentifier]: fieldName, [FieldSettingKeys.DisplayType]: "CORE" });
        }
    }

    for (const field of fieldSettings.additionalFields.value) {
        serverFieldSettings.push({ [FieldSettingKeys.FieldIdentifier]: field.referenceName });
        if (field.referenceName === CoreFieldRefNames.Title) {
            addedTitleField = true;
        }
    }

    // Title is a required field, so always add it
    if (!addedTitleField) {
        serverFieldSettings.push({ [FieldSettingKeys.FieldIdentifier]: CoreFieldRefNames.Title });
    }

    return serverFieldSettings;
}
